import React from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Paper,
  IconButton,
  Tooltip
} from '@mui/material';
import { Close, InfoOutlined } from '@mui/icons-material';
import { format } from 'date-fns';
import StatusChip from '../common/StatusChip';

const TaskDetailDialog = ({ task, open, onClose, onRetry }) => {
  if (!task) return null;
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Task Details
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {/* Task Header */}
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ mr: 2 }}></Typography>
            <Typography variant="h6" sx={{ mr: 2 }}>
              {task.taskId}
            </Typography>
            <StatusChip status={task.status} />
          </Box>
          
          {/* Task Details */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Basic Information
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Execution ID</Typography>
                <Typography variant="body2">{task.executionId}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Job Definition ID</Typography>
                <Typography variant="body2">{task.jobDefinitionId}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Data Source</Typography>
                <Typography variant="body2">{task.dataSource}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Data Category</Typography>
                <Typography variant="body2">{task.dataCategory}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Source/Store ID</Typography>
                <Typography variant="body2">{task.sourceIdentifier}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Client ID</Typography>
                <Typography variant="body2">{task.clientId}</Typography>
              </Box>
            </Box>
          </Paper>
          
          {/* Time Range */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Time Range
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Start Date/Time</Typography>
                <Typography variant="body2">
                  {format(new Date(task.timeRangeStart), 'yyyy-MM-dd HH:mm:ss')}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">End Date/Time</Typography>
                <Typography variant="body2">
                  {format(new Date(task.timeRangeEnd), 'yyyy-MM-dd HH:mm:ss')}
                </Typography>
              </Box>
            </Box>
          </Paper>
          
          {/* Processing Details */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Processing Information
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Start Time</Typography>
                <Typography variant="body2">
                  {task.startTime ? format(new Date(task.startTime), 'yyyy-MM-dd HH:mm:ss') : '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">End Time</Typography>
                <Typography variant="body2">
                  {task.endTime ? format(new Date(task.endTime), 'yyyy-MM-dd HH:mm:ss') : '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Duration</Typography>
                <Typography variant="body2">
                  {task.processingDuration ? `${Math.round(task.processingDuration / 1000)} seconds` : '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Records Processed</Typography>
                <Typography variant="body2">{task.recordsProcessed || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Retry Count</Typography>
                <Typography variant="body2">{task.retryCount || '0'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Priority</Typography>
                <Typography variant="body2">{task.priority || '1'}</Typography>
              </Box>
            </Box>
          </Paper>
          
          {/* Validation Results (if applicable) */}
          {(task.validationStatus !== null || task.validationDiscrepancies !== null) && (
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Validation Results
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Validation Status</Typography>
                  <Typography variant="body2">
                    {task.validationStatus === true ? 'Passed' : 
                     task.validationStatus === false ? 'Failed' : 'Not Validated'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Discrepancies Found</Typography>
                  <Typography variant="body2">{task.validationDiscrepancies || '0'}</Typography>
                </Box>
                {task.validationNotes && (
                  <Box sx={{ gridColumn: '1 / span 2' }}>
                    <Typography variant="caption" color="text.secondary">Validation Notes</Typography>
                    <Typography variant="body2">{task.validationNotes}</Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          )}
          
          {/* Error Details */}
          {task.status === 'FAILED' && task.errorDetails && (
            <Paper sx={{ p: 2, mb: 3, backgroundColor: 'error.light' }}>
              <Typography variant="subtitle2" gutterBottom color="error.contrastText">
                Error Details
              </Typography>
              <Box sx={{ 
                p: 2, 
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                borderRadius: 1,
                maxHeight: '150px',
                overflow: 'auto'
              }}>
                <Typography variant="body2" component="pre" sx={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                  {task.errorDetails}
                </Typography>
              </Box>
            </Paper>
          )}
          
          {/* Raw Task Object (for debugging) */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Raw Task Definition
              <Tooltip title="Advanced technical information">
                <InfoOutlined fontSize="small" sx={{ ml: 1, verticalAlign: 'middle' }} />
              </Tooltip>
            </Typography>
            <Paper sx={{ 
              p: 2, 
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              maxHeight: '200px',
              overflow: 'auto'
            }}>
              <Typography component="pre" variant="body2" sx={{ m: 0 }}>
                {JSON.stringify(task, null, 2)}
              </Typography>
            </Paper>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        {task.status === 'FAILED' && (
          <Button color="primary" variant="contained" onClick={onRetry}>
            Retry Task
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TaskDetailDialog;